import type { ListBuilderColumn } from '../../types';

/**
 * Image column - displays item primary image
 */
export const createImageColumn = (): ListBuilderColumn => ({
	id: 'image',
	width: 60,
	fields: [
		{
			id: 'item_image',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'primaryImageUrl',
					objectTable: 'client_shop',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				imageOptions: {
					size: 'small',
					enableViewer: true,
				},
			},
			displayStyle: 'image',
		},
	],
	headers: [
		{
			id: 'image_header',
			text: '',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Item details column - name with link and manufacturer (theme colors)
 */
export const createItemDetailsColumnTheme = (): ListBuilderColumn => ({
	id: 'item_details',
	fields: [
		{
			id: 'description',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'item',
				},
				defaultValue: 'Unknown Item',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_desc',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'manufacturer',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'item_header',
			text: 'Name',
		},
		{
			id: 'header_1751545013388',
			text: 'Manufacturer',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Item details column - name with link and manufacturer (static colors, extra line break)
 */
export const createItemDetailsColumnStatic = (): ListBuilderColumn => ({
	id: 'item_details',
	fields: [
		{
			id: 'description',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'item',
				},
				defaultValue: 'Unknown Item',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_desc',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'manufacturer',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'static',
					staticColor: {
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'item_header',
			text: 'Name',
		},
		{
			id: 'header_1751545013388',
			text: 'Manufacturer',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Category column with conditional externalId/externalId2 (for quote_shop_default)
 */
export const createCategoryColumnConditional = (): ListBuilderColumn => ({
	id: 'category',
	minWidth: 75,
	fields: [
		{
			id: 'field_1751545075287',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'externalId2',
								objectTable: 'item',
							},
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: {
						fieldName: 'externalId',
						objectTable: 'item',
					},
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545183916',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'category_field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'category',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1752779253753',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1752779256147',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'subcategory',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'header_1751545047862',
			text: 'Item #',
		},
		{
			id: 'category_header',
			text: 'Category',
		},
		{
			id: 'header_1752779245678',
			text: 'Subcategory',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'externalId',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Category column with simple externalId (static colors, extra line break)
 */
export const createCategoryColumnStatic = (): ListBuilderColumn => ({
	id: 'category',
	minWidth: 75,
	fields: [
		{
			id: 'field_1751545075287',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'externalId',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545183916',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'static',
					staticColor: {
						bg: 'transparent',
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'category_field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'category',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1752779253753',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'static',
					staticColor: {
						bg: 'transparent',
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1752779256147',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'subcategory',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'header_1751545047862',
			text: 'Item #',
		},
		{
			id: 'category_header',
			text: 'Category',
		},
		{
			id: 'header_1752779245678',
			text: 'Subcategory',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'externalId',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Size column - sizeCount and unitOfMeasure (theme colors)
 */
export const createSizeColumnTheme = (): ListBuilderColumn => ({
	id: 'size',
	fields: [
		{
			id: 'price_prefix',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'sizeCount',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					decimalPlaces: 0,
				},
			},
			displayStyle: 'number',
		},
		{
			id: 'line_break_size',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'unit_of_measure',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'unitOfMeasure',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Size',
		},
		{
			id: 'unit_header',
			text: 'Unit',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'sizeCount',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Size column - sizeCount and unitOfMeasure (static colors, extra line break)
 */
export const createSizeColumnStatic = (): ListBuilderColumn => ({
	id: 'size',
	fields: [
		{
			id: 'price_prefix',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'sizeCount',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					decimalPlaces: 0,
				},
			},
			displayStyle: 'number',
		},
		{
			id: 'line_break_size',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'unit_of_measure',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'unitOfMeasure',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'static',
					staticColor: {
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Size',
		},
		{
			id: 'unit_header',
			text: 'Unit',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'sizeCount',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Pricing column - price and minimumPrice (theme colors, sortable)
 */
export const createPricingColumnTheme = (): ListBuilderColumn => ({
	id: 'column_1751545591402',
	fields: [
		{
			id: 'field_1751545620039',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'price',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1751545636426',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545638994',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'minimumPrice',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{
			id: 'header_1751545591402',
			text: 'Target',
		},
		{
			id: 'header_1751545611857',
			text: 'Min.',
		},
	],
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'price',
			objectTable: 'item',
		},
	],
	alignment: 'right',
	verticalAlignment: 'top',
});

/**
 * Pricing column - price and minimumPrice (static colors, sortable)
 */
export const createPricingColumnStatic = (): ListBuilderColumn => ({
	id: 'column_1751545591402',
	fields: [
		{
			id: 'field_1751545620039',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'price',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1751545636426',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'static',
					staticColor: {
						bg: 'transparent',
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545638994',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'minimumPrice',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{
			id: 'header_1751545591402',
			text: 'Target',
		},
		{
			id: 'header_1751545611857',
			text: 'Min.',
		},
	],
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'price',
			objectTable: 'item',
		},
	],
	alignment: 'right',
	verticalAlignment: 'top',
});

/**
 * Stock column - quantityOnHand, quantityOnOrder, soonestExpirationDate (theme colors)
 */
export const createStockColumnTheme = (): ListBuilderColumn => ({
	id: 'stock',
	width: 100,
	fields: [
		{
			id: 'stock_status',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnHand',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-stock-status',
			},
		},
		{
			id: 'field_1751545326188',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545329661',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnOrder',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545336957',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545339632',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'stock_header',
			text: 'Available',
		},
		{
			id: 'header_1751545264070',
			text: 'On Order',
		},
		{
			id: 'header_1751545271709',
			text: 'Soonest Exp.',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Stock column - quantityOnHand, quantityOnOrder, soonestExpirationDate (static colors)
 */
export const createStockColumnStatic = (): ListBuilderColumn => ({
	id: 'stock',
	width: 100,
	fields: [
		{
			id: 'stock_status',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnHand',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-stock-status',
			},
		},
		{
			id: 'field_1751545326188',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'static',
					staticColor: {
						bg: 'transparent',
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545329661',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnOrder',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545336957',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'static',
					staticColor: {
						bg: 'transparent',
						text: '#6b7280',
					},
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545339632',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'stock_header',
			text: 'Available',
		},
		{
			id: 'header_1751545264070',
			text: 'On Order',
		},
		{
			id: 'header_1751545271709',
			text: 'Soonest Exp.',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Last invoice data column - invoice link, last price, last date (theme colors)
 */
export const createLastInvoiceDataColumnTheme = (): ListBuilderColumn => ({
	id: 'last_invoice_data',
	minWidth: 90,
	fields: [
		{
			id: 'last_invoice_id',
			link: {
				type: 'object',
				enabled: true,
				idField: 'lastInvoiceId',
				objectTable: 'invoice',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceExternalId',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_invoice',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressLastPrice',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'line_break_price',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_invoice_date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceDate',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'date',
			dateOptions: {
				format: 'medium',
			},
		},
	],
	headers: [
		{
			id: 'last_invoice_header',
			text: 'Last Invoice',
		},
		{
			id: 'last_price_header',
			text: 'Last Price',
		},
		{
			id: 'last_date_header',
			text: 'Date',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'lastInvoiceDate',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Sales data column - monthly units sold with static headers (theme colors)
 */
export const createSalesDataColumnTheme = (): ListBuilderColumn => ({
	id: 'sales_data',
	minWidth: 90,
	fields: [
		{
			id: 'current_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsMtd',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_current',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsLastMonth',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_last',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'two_months_ago',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsTwoMonthsAgo',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'current_month_header',
			text: 'Current Month',
		},
		{
			id: 'last_month_header',
			text: 'Last Month',
		},
		{
			id: 'two_months_ago_header',
			text: 'Two Months Ago',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'addressSoldUnitsMtd',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Quantity input column - custom component for quote shop
 */
export const createQuantityColumn = (): ListBuilderColumn => ({
	id: 'quantity',
	width: 150,
	fields: [
		{
			id: 'shop_quantity',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: '_id',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'quote-shop-quantity-input',
			},
		},
	],
	headers: [
		{
			id: 'quantity_header',
			text: 'Qty',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Price input column - custom component for quote shop
 */
export const createPriceInputColumn = (): ListBuilderColumn => ({
	id: 'price',
	width: 100,
	fields: [
		{
			id: 'shop_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: '_id',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'quote-shop-price-input',
			},
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Price',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Standard columns for quote_shop_default list
 */
export const createQuoteShopDefaultColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnTheme(),
	createCategoryColumnConditional(),
	createSizeColumnTheme(),
	createPricingColumnTheme(),
	createStockColumnTheme(),
	createLastInvoiceDataColumnTheme(),
	createSalesDataColumnTheme(),
	createQuantityColumn(),
	createPriceInputColumn(),
];

/**
 * Standard columns for secondary quote shop lists
 */
export const createQuoteShopStandardColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnStatic(),
	createCategoryColumnStatic(),
	createSizeColumnStatic(),
	createPricingColumnStatic(),
	createStockColumnStatic(),
	createLastInvoiceDataColumnTheme(),
	createSalesDataColumnTheme(),
	createQuantityColumn(),
	createPriceInputColumn(),
];
